import { financeScale, financeScaleInvert } from './finance-time-scale';
import { getEasternTimeFromDate, getLocalTimeFromEasternDate } from './daylight-savings-time';
import { FIRST_HOUR,	FIRST_MINUTE,	FIRST_SECOND,	SECONDS_PER_FINANCE_DAY, SECONDS_PER_HOUR, SECONDS_PER_FIVE_MINUTES, SECONDS_PER_MINUTE,
	SECONDS_PER_FINANCE_WEEK,  DAYS_PER_WEEK, SHIFT_MONDAY_TO_DAY_0, DECEMBER, MONTHS_PER_QUARTER, SECONDS_PER_QUARTER_HOUR,
	SECONDS_PER_HALF_MINUTE, SECONDS_PER_BI_HOUR, YEARS_PER_DECADE } from './finance-constants';


/**
 * Return an array of ticks that represent "neat" tick intervals on a decade
 * basis that encompass startTick and stopTick.  The first tick will be the first trading tick of January of the decade
 * following startTick.  The last returned tick will be the first tick of the decade of stopTick.
 *
 * @param {Integer} startTick The financeTimeScale tick representing the beginning of the data series
 * @param {Integer} stopTick The financeTimeScale tick representing the end of the data series
 * @returns {Array} An array of uniformly-ish distributed ticks to use for the axis.
 */
export function decadelyInterval(startTick, stopTick) {
	let firstTickDecade = YEARS_PER_DECADE * ((getEasternTimeFromDate(financeScaleInvert(startTick)).getFullYear() / YEARS_PER_DECADE) | 0) + YEARS_PER_DECADE;
	let lastTickDecade = YEARS_PER_DECADE * ((getEasternTimeFromDate(financeScaleInvert(stopTick)).getFullYear() / YEARS_PER_DECADE) | 0);
	let decades = [];
	for (let i = firstTickDecade; i<=lastTickDecade; i+=YEARS_PER_DECADE) {
		//eslint-disable-next-line no-magic-numbers
		decades.push(financeScale(new Date(i-1, DECEMBER, 31, 23, 59, 59, 999))+1);
	}

	return decades;
}

/**
 * Return an array of ticks that represent "neat" tick intervals on a yearly
 * basis that encompass startTick and stopTick.  The first tick will be the first trading tick of January of the year
 * following startTick.  The last returned tick will be the first tick of the year of stopTick.
 *
 * @param {Integer} startTick The financeTimeScale tick representing the beginning of the data series
 * @param {Integer} stopTick The financeTimeScale tick representing the end of the data series
 * @returns {Array} An array of uniformly-ish distributed ticks to use for the axis.
 */
export function yearlyInterval(startTick, stopTick) {
	let firstTickYear = getEasternTimeFromDate(financeScaleInvert(startTick)).getFullYear() + 1;
	let lastTickYear = getEasternTimeFromDate(financeScaleInvert(stopTick)).getFullYear();
	let years = [];
	for (let i = firstTickYear; i<=lastTickYear; i++) {
		//eslint-disable-next-line no-magic-numbers
		years.push(financeScale(new Date(i-1, DECEMBER, 31, 23, 59, 59))+1);
	}

	return years;
}

/**
 * Same as yearlyInterval but with quarterly intervals starting on the tick of the first trading day
 * each quarter: i.e. first tick in January, April, July, and October
 * @see yearlyInterval
 */
export function quarterlyInterval(startTick, stopTick) {
	let firstTickDate = getEasternTimeFromDate(financeScaleInvert(startTick));
	let lastTickDate = getEasternTimeFromDate(financeScaleInvert(stopTick));
	let year = firstTickDate.getFullYear();
	//get the first Jan/Apr/Jul/Oct month following the first month
	let firstMonth = MONTHS_PER_QUARTER * (1 + ((firstTickDate.getMonth() / MONTHS_PER_QUARTER) | 0));
	let months = [];
	for (let month = firstMonth; getEasternTimeFromDate(new Date(year, month, 1)) <= lastTickDate; month+=MONTHS_PER_QUARTER) {
		months.push(financeScale(new Date(year, month, 1))+1);
	}

	return months;
}

/**
 * Same as yearlyInterval but with monthly intervals starting on the tick of the first trading day of the month
 * @see yearlyInterval
 */
export function monthlyInterval(startTick, stopTick) {
	let firstTickDate = getEasternTimeFromDate(financeScaleInvert(startTick));
	let lastTickDate = getEasternTimeFromDate(financeScaleInvert(stopTick));
	let year = firstTickDate.getFullYear();
	let months = [];
	for (let month = firstTickDate.getMonth() + 1; getEasternTimeFromDate(new Date(year, month, 1)) <= lastTickDate; month++) {
		months.push(financeScale(new Date(year, month, 1))+1);
	}

	return months;
}

/**
 * Same as yearlyInterval but with weekly intervals starting on Monday
 * @see yearlyInterval
 */
export function weeklyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_FINANCE_WEEK);
}

/**
 * Same as yearlyInterval but with daily intervals starting at the beginning of the day
 * @see yearlyInterval
 */
export function dailyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_FINANCE_DAY);
}

/**
 * Same as yearlyInterval but with intervals every 2 hours starting at the beginning of the day
 * @see yearlyInterval
 */
export function bihourlyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_BI_HOUR);
}

/**
 * Same as yearlyInterval but with intervals every hour starting at the top of the hour :00
 * @see yearlyInterval
 */
export function hourlyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_HOUR);
}

/**
 * Same as yearlyInterval but with intervals every 15 minutes starting at the closest quarter-hour
 * e.g. :00, :15, :30, :45
 * @see yearlyInterval
 */
export function quarterHourlyInterval(startTick, stopTick) {
	//find the first quarter-hour after startTick
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_QUARTER_HOUR);
}

/**
 * Same as yearlyInterval but with intervals every 15 minutes starting at the closest quarter-hour
 * e.g. :00, :05, :10, :15
 * @see yearlyInterval
 */
export function fiveMinutelyInterval(startTick, stopTick) {
	//find the first quarter-hour after startTick
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_FIVE_MINUTES);
}

/**
 * Same as yearlyInterval but with intervals every minute starting at the top of the minute
 * @see yearlyInterval
 */
export function minutelyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_MINUTE);
}

/**
 * Same as yearlyInterval but with intervals every 30 seconds on :00 and :30 second boundaries
 * @see yearlyInterval
 */
export function halfMinutelyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, SECONDS_PER_HALF_MINUTE);
}

/**
 * Same as yearlyInterval but with intervals every second
 * @see yearlyInterval
 */
export function secondlyInterval(startTick, stopTick) {
	return intervalsWeeklyOrLess(startTick, stopTick, 1);
}

/*
 * Common function to calculate intervals that span a week or less where the seconds
 * in an interval are consistent
 */
function intervalsWeeklyOrLess(startTick, stopTick, interval) {
	let d = getEasternTimeFromDate(financeScaleInvert(startTick));
	let baseTime;
	if (interval > SECONDS_PER_FINANCE_DAY) {
		//Anything over a day, start at the beginning of the current finance week
		//shift to the Monday on or before the current day represented by the tick
		let dayShift = (d.getDay() + SHIFT_MONDAY_TO_DAY_0) % DAYS_PER_WEEK;
		baseTime = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayShift, FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND);
	}
	else if (interval > SECONDS_PER_HOUR) {
		//Anything over an hour, start at the beginning of the current finance day
		baseTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND);
	}
	else if (interval >= SECONDS_PER_MINUTE) {
		//Anything over an hour, start at the beginning of the hour
		baseTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours());
	}
	else if (interval > 1) {
		//Anything over a second, start at the beginning of the minute
		baseTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes());
	}
	else {
		//For a second or less, start on the second
		baseTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
	}

	baseTime = getLocalTimeFromEasternDate(baseTime);

	//Get the first tick that is a multiple of the interval after the base time
	let firstTick = financeScale(baseTime);
	while (firstTick <= startTick) {
		firstTick += interval;
	}
	let intervalTicks = [];
	for (let currentTick = firstTick; currentTick <= stopTick; currentTick+=interval) {
		intervalTicks.push(currentTick);
	}

	return intervalTicks;
}
